import { useState } from "react";
import { Alert, NumberInput } from "@/deps/ui-library";
import { api } from "@/api";
import { useCheckoutQuote } from "@/api/queries";
import type {
  CheckoutChoice,
  CheckoutOffer,
  CheckoutOrder,
  PaymentMethod,
  SubscriptionAccount,
} from "@/api/types";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { ChangeDrawer } from "./ChangeDrawer";
import { onlineMethods } from "./paymentMethods";

const money = (currency: string, amount: number) => `${currency} ${formatAmount(amount)}`;

/**
 * A change that applies at once (suite ADR-0099): a higher plan, the Reporting layer added, more users. RigoSub prices
 * it as the difference for the rest of the period already paid, by the day this month; paying goes through the payment
 * page, and only a paid order changes anything. The renewal is then priced on the new terms.
 */
export function ChangeNowDrawer({
  title,
  account,
  offer,
  start,
  onOrder,
  onClose,
}: {
  title: string;
  account: SubscriptionAccount;
  offer: CheckoutOffer;
  start: CheckoutChoice;
  /** The order placed, and how to place it again through another method (suite ADR-0102). */
  onOrder: (
    order: CheckoutOrder,
    replace: (method: PaymentMethod) => Promise<CheckoutOrder>,
  ) => void;
  onClose: () => void;
}) {
  const [choice, setChoice] = useState<CheckoutChoice>(start);
  const { data: quote, error, isFetching } = useCheckoutQuote(choice);
  const plan = offer.plans.find((p) => p.code === choice.planCode);
  const yearly = choice.cycle === "YEARLY";
  const paidSeats = account.includedUsers + account.current.extraUsers;
  const fewestExtra = plan ? Math.max(0, paidSeats - plan.includedUsers) : 0;
  const renewal = quote ? formatDate(quote.renewsOn) : "the renewal";
  const payable = onlineMethods(quote?.paymentMethods ?? offer.paymentMethods).length > 0;

  return (
    <ChangeDrawer
      title={title}
      description="Applies straight away. You pay the difference for the rest of the period already paid, by the day."
      rows={
        quote && !error
          ? [
              { label: "Takes effect", value: "Today" },
              {
                label: "Charged now",
                value: `${money(quote.currency, quote.total)}${quote.taxPercent > 0 ? " with VAT" : ""}`,
              },
              {
                label: `From ${renewal}`,
                value: `${money(quote.currency, quote.perCycle)} ${yearly ? "a year" : "a month"}${quote.taxPercent > 0 ? " plus VAT" : ""}`,
              },
            ]
          : []
      }
      confirm={{
        label: quote && !error ? `Pay ${money(quote.currency, quote.total)}` : "Pay",
        disabled: !quote || !!error || isFetching || !payable,
        onConfirm: async () => {
          const order = (method?: PaymentMethod) =>
            api.subscription.placeOrder({
              choice,
              billing: account.billing,
              expectedTotal: quote!.total,
              method: method ?? null,
            });
          onOrder(await order(), order);
        },
      }}
      onClose={onClose}
    >
      {plan?.extraUser ? (
        <NumberInput
          label="Extra users"
          helperText={`Beyond the ${plan.includedUsers} ${plan.name} includes, ${money(offer.currency, yearly ? plan.extraUser.yearly : plan.extraUser.monthly)} each ${yearly ? "a year" : "a month"}. You have ${paidSeats} users paid for.`}
          min={fewestExtra}
          max={500}
          value={String(choice.extraUsers)}
          onValueChange={(details: { valueAsNumber: number }) =>
            setChoice({
              ...choice,
              extraUsers: Number.isFinite(details.valueAsNumber)
                ? Math.max(fewestExtra, Math.trunc(details.valueAsNumber))
                : fewestExtra,
            })
          }
        />
      ) : null}

      {error ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "warning" }}
          description={error instanceof Error ? error.message : "This change can't be priced."}
        />
      ) : quote ? (
        <div
          className="border-line-soft flex flex-col rounded-md border text-sm"
          aria-label="What you pay now"
        >
          {quote.lines.map((line) => (
            <div
              key={line.label}
              className="border-line-soft flex justify-between gap-4 border-b px-3 py-2"
            >
              <span className="flex min-w-0 flex-col">
                <span className="text-fg">{line.label}</span>
                <span className="text-fg-muted text-xs">{line.detail}</span>
              </span>
              <span className="text-fg num shrink-0">{formatAmount(line.amount)}</span>
            </div>
          ))}
          <div className="text-fg-muted flex justify-between gap-4 px-3 pt-2">
            <span>Subtotal</span>
            <span className="num">{formatAmount(quote.subtotal)}</span>
          </div>
          <div className="text-fg-muted flex justify-between gap-4 px-3 pt-1">
            <span>{quote.taxPercent > 0 ? `VAT ${quote.taxPercent}%` : "No VAT"}</span>
            <span className="num">{formatAmount(quote.tax)}</span>
          </div>
          <div className="text-fg flex justify-between gap-4 px-3 py-2 font-semibold">
            <span>Total today</span>
            <span className="num">{money(quote.currency, quote.total)}</span>
          </div>
        </div>
      ) : null}

      {!payable ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "default" }}
          description="Paying online isn't open yet. Write to billing to make this change."
        />
      ) : null}
    </ChangeDrawer>
  );
}
